import React, { useEffect, useMemo, useState } from "react";
import { CommentExplorerMode, CommentScopeFilter } from "./types";

interface CommentExplorerToolbarProps {
  mode: CommentExplorerMode;
  searchQuery: string;
  scopeFilter: CommentScopeFilter;
  onModeChange: (mode: CommentExplorerMode) => void;
  onSearchChange: (query: string) => void;
  onScopeFilterChange: (filter: CommentScopeFilter) => void;
}

const modeOptions: Array<{ id: CommentExplorerMode; label: string; title?: string }> = [
  { id: "overview", label: "Overview", title: "Reddit-style threaded comment flow" },
  { id: "lanes", label: "Lanes", title: "Navigate one comment level at a time" },
  { id: "reader", label: "Reader", title: "Read the selected branch calmly" },
  { id: "map", label: "Map", title: "Visual branch map" },
  { id: "recent", label: "Recent", title: "Newest comment activity" },
];

const mobilePrimaryModeOptions = modeOptions.filter((option) => option.id === "reader" || option.id === "recent");
const mobileAdvancedModeOptions = modeOptions.filter((option) => option.id === "overview" || option.id === "lanes" || option.id === "map");
const mobileAdvancedModeIds = new Set<CommentExplorerMode>(mobileAdvancedModeOptions.map((option) => option.id));

const scopeOptions: Array<{ id: CommentScopeFilter; label: string }> = [
  { id: "all", label: "All comments" },
  { id: "currentEdit", label: "Current edit" },
  { id: "gistWide", label: "Gist-wide" },
  { id: "olderEdit", label: "Older edits" },
];

const CommentExplorerToolbar: React.FC<CommentExplorerToolbarProps> = ({
  mode,
  searchQuery,
  scopeFilter,
  onModeChange,
  onSearchChange,
  onScopeFilterChange,
}) => {
  const [advancedViewsOpen, setAdvancedViewsOpen] = useState(false);
  const activeAdvancedMode = mobileAdvancedModeIds.has(mode);
  const showAdvancedViews = advancedViewsOpen || activeAdvancedMode;

  useEffect(() => {
    if (activeAdvancedMode) {
      setAdvancedViewsOpen(true);
    }
  }, [activeAdvancedMode]);

  const advancedToggleLabel = useMemo(() => {
    if (showAdvancedViews) return "Hide advanced views";
    return "Show advanced views";
  }, [showAdvancedViews]);

  const renderModeButton = (option: { id: CommentExplorerMode; label: string; title?: string }) => (
    <button
      key={option.id}
      type="button"
      className={`comment-explorer-tab ${mode === option.id ? "is-active" : ""}`}
      title={option.title}
      onClick={() => onModeChange(option.id)}
    >
      {option.label}
    </button>
  );

  return (
    <div className="comment-explorer-toolbar">
      <div className="comment-explorer-mode-tabs comment-explorer-mode-tabs--desktop" role="tablist" aria-label="Comment view mode">
        {modeOptions.map(renderModeButton)}
      </div>

      <div className="comment-explorer-mobile-mode-panel" aria-label="Mobile comment view mode">
        <div className="comment-explorer-mobile-mode-heading">
          <span>Main views</span>
          <strong>Read first, inspect later</strong>
        </div>
        <div className="comment-explorer-mobile-primary-tabs" role="tablist" aria-label="Primary mobile comment views">
          {mobilePrimaryModeOptions.map(renderModeButton)}
        </div>
        <button
          type="button"
          className={`comment-explorer-advanced-toggle ${showAdvancedViews ? "is-open" : ""}`}
          aria-expanded={showAdvancedViews}
          onClick={() => setAdvancedViewsOpen((open) => !open)}
        >
          <span>{advancedToggleLabel}</span>
          <span aria-hidden="true">{showAdvancedViews ? "−" : "+"}</span>
        </button>
        {showAdvancedViews && (
          <div className="comment-explorer-mobile-advanced-tabs" role="tablist" aria-label="Advanced mobile comment views">
            {mobileAdvancedModeOptions.map(renderModeButton)}
          </div>
        )}
      </div>

      <label className="comment-explorer-search">
        <span>Search</span>
        <input
          value={searchQuery}
          placeholder="Search comments or authors…"
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </label>

      <div className="comment-explorer-scope-filters" aria-label="Comment scope filter">
        {scopeOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            className={`comment-explorer-filter ${scopeFilter === option.id ? "is-active" : ""}`}
            onClick={() => onScopeFilterChange(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CommentExplorerToolbar;
